/**
 * Amazon-style Add to Cart + Inline Variant Toggle (ES5 compatible)
 * - Clicking "Add to cart" adds item via AJAX and opens cart drawer
 * - Quantity is managed in the cart drawer, not on the card
 * - Enhances SearchSpring cards with variant pills on load
 *
 * ⚠️  SCOPE WARNING: despite the "amazon-atc" name, this file owns a
 *     CAPTURE-PHASE click delegation handler on `document` that runs
 *     BEFORE every other click listener on the page. It intercepts:
 *       - Quantity stepper +/- buttons
 *       - Variant toggle buttons
 *       - Add-to-cart buttons (.js-amazon-atc-btn, .js-quick-add)
 *       - Image/title links inside cart drawer recommendation cards
 *         (to keep users in the drawer for SearchSpring cards)
 *
 *     If clicks anywhere on the site mysteriously stop navigating, CHECK
 *     THIS FILE FIRST - the capture-phase preventDefault/stopPropagation
 *     can block events before they reach any other handler.
 *     See `document.addEventListener('click', ..., true)` block below.
 *
 * NOTE: Avada Free Gifts has a /cart.js polling bug (~18 req/sec).
 * The throttle for that lives in avada-cart-throttle.liquid which
 * must be included in theme.liquid <head> BEFORE Avada loads.
 */
(function() {
  var SEL = {
    wrapper: '.js-amazon-atc',
    addBtn: '.js-amazon-atc-btn',
    variantBtn: '.js-variant-toggle-btn',
    variantGroup: '.js-variant-toggle',
    priceContainer: '.js-variant-price'
  };

  // Track in-flight requests to prevent sync from overwriting optimistic state
  var inFlightCount = 0;

  // Sequential queue for cart mutations — prevents race conditions and rate limiting
  // Each request has a 10s safety timeout so the queue never gets permanently stuck
  var cartQueue = Promise.resolve();
  function enqueueCartRequest(fn) {
    cartQueue = cartQueue.then(function() {
      return new Promise(function(resolve) {
        var done = false;
        var timer = setTimeout(function() {
          if (!done) { done = true; resolve(); }
        }, 10000);
        try {
          var result = fn();
          if (result && typeof result.then === 'function') {
            result.then(function() {
              if (!done) { done = true; clearTimeout(timer); resolve(); }
            }, function() {
              if (!done) { done = true; clearTimeout(timer); resolve(); }
            });
          } else {
            if (!done) { done = true; clearTimeout(timer); resolve(); }
          }
        } catch (e) {
          if (!done) { done = true; clearTimeout(timer); resolve(); }
        }
      });
    });
    return cartQueue;
  }

  function openCartDrawer() {
    // [May 2026] Mobile bypass restored - mobile uses /cart page instead.
    if (window.innerWidth <= 768) return;
    var cd = document.querySelector('cart-drawer') || document.querySelector('[data-cart-drawer]');
    if (cd) {
      if (typeof cd.open === 'function') { cd.open(); return; }
      if (typeof cd.show === 'function') { cd.show(); return; }
    }
    var trigger = document.querySelector('[data-cart-drawer-open]')
      || document.querySelector('.js-cart-drawer-open')
      || document.querySelector('.js-open-cart-drawer');
    if (trigger) { trigger.click(); return; }
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
  }

  // --- Section names Shopify will render for us ---
  var SECTION_KEYS = ['cart-drawer', 'cart-icon-bubble'];

  // Extract product handle from URLs like:
  //   /products/delyco-chestnut-180g
  //   /collections/newly-added/products/delyco-chestnut-180g
  //   /collections/newly-added/products/delyco-chestnut-180g?variant=123
  // Without this, .replace('/products/', '') on collection URLs produces
  // "/collections/newly-addeddelyco-chestnut-180g" → 404 on /products/.js
  function extractHandle(url) {
    if (!url) return '';
    var match = url.match(/\/products\/([^?#\/]+)/);
    return match ? match[1] : '';
  }

  // Apply section HTML that came back from Shopify (either from cart/add.js
  // with ?sections or from a standalone sections fetch).
  //
  // ALWAYS uses surgical updates: cart items, footer/totals, and bubble only.
  // NEVER replaces entire cart-drawer innerHTML — doing so destroys Avada
  // Free Gifts' milestone bar DOM, which triggers Avada's recovery loop
  // (infinite /cart.js?ts=... polling at ~18 req/sec).
  function applySections(data, opts) {
    if (!data) return;
    opts = opts || {};

    // Flag so external MutationObservers (Avada reinject) can skip our updates
    window.__AP_SECTIONS_UPDATING__ = true;

    // --- Update cart-icon-bubble (skip if requests in-flight to prevent stale overwrites) ---
    var bubbleHtml = data['cart-icon-bubble'];
    if (bubbleHtml && inFlightCount <= 1) {
      var curBub = document.getElementById('cart-icon-bubble');
      if (curBub) {
        var bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        var wrapper = bubbleDoc.querySelector('.shopify-section');
        curBub.innerHTML = wrapper ? wrapper.innerHTML : bubbleHtml;
      }
    }

    var drawerHtml = data['cart-drawer'];
    if (!drawerHtml) return;
    var drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
    var drawerEl = document.querySelector('cart-drawer') || document.querySelector('#CartDrawer');
    if (!drawerEl) return;

    // Surgical update: items + footer + totals only.
    // Avada milestone bar and SS recommendation widget are left untouched.
    var scrollEl = drawerEl.querySelector('.drawer__content');
    var savedScroll = scrollEl ? scrollEl.scrollTop : 0;

    // 1) Cart items list
    var cartItemsSels = ['cart-items', '.cart-items', '.js-cart-items', '#CartDrawer-CartItems'];
    for (var ci = 0; ci < cartItemsSels.length; ci++) {
      var curItems = drawerEl.querySelector(cartItemsSels[ci]);
      var newItems = drawerDoc.querySelector(cartItemsSels[ci]);
      if (curItems && newItems) {
        curItems.innerHTML = newItems.innerHTML;

        // Sync data-empty attribute from server-rendered HTML so the drawer
        // switches out of "empty cart" mode when the first item is added.
        var isEmpty = !!curItems.querySelector('.js-cart-empty');
        curItems.setAttribute('data-empty', String(isEmpty));

        // Sync wrapper layout classes for empty↔non-empty transition.
        // Without this, the first item added stays invisible behind the
        // flex centering / empty-state classes.
        var contentEl = drawerEl.querySelector('.drawer__content');
        if (contentEl) {
          contentEl.classList.toggle('drawer__content--flex', isEmpty);
          contentEl.classList.toggle('drawer__empty', isEmpty);
          contentEl.classList.toggle('drawer__empty-with-promotions',
            isEmpty && !!drawerEl.querySelector('.cart-drawer__promoted-products-container'));
        }
        var cartDrawerContentEl = drawerEl.querySelector('.cart-drawer__content');
        if (cartDrawerContentEl) {
          cartDrawerContentEl.classList.toggle('grow', isEmpty);
          cartDrawerContentEl.classList.toggle('flex', isEmpty);
          cartDrawerContentEl.classList.toggle('items-center', isEmpty);
          cartDrawerContentEl.classList.toggle('justify-center', isEmpty);
          cartDrawerContentEl.classList.toggle('w-full', isEmpty);
        }

        // Sync the "Products (N)" header — only rendered when cart is non-empty
        var curHeader = drawerEl.querySelector('.cart-drawer__header');
        var newHeader = drawerDoc.querySelector('.cart-drawer__header');
        if (!curHeader && newHeader) {
          curItems.parentNode.insertBefore(newHeader.cloneNode(true), curItems);
        } else if (curHeader && newHeader) {
          curHeader.innerHTML = newHeader.innerHTML;
        } else if (curHeader && !newHeader) {
          curHeader.remove();
        }

        // Sync free shipping notice (only rendered when cart is non-empty)
        var curShipping = drawerEl.querySelector('free-shipping-notice, .free-shipping-notice');
        var newShipping = drawerDoc.querySelector('free-shipping-notice, .free-shipping-notice');
        if (!curShipping && newShipping && cartDrawerContentEl) {
          cartDrawerContentEl.insertBefore(newShipping.cloneNode(true), cartDrawerContentEl.firstChild);
        } else if (curShipping && newShipping) {
          curShipping.outerHTML = newShipping.outerHTML;
        } else if (curShipping && !newShipping) {
          curShipping.remove();
        }

        break;
      }
    }

    // 2-4) Footer / totals — skip when called from the delayed freebie refresh
    //       (ap-cart-totals.js already handles totals via its own /cart.js fetch,
    //        replacing footer innerHTML causes a visible flash + resets order note)
    if (!opts.skipFooter) {
      var footerSels = ['.drawer__footer', '.cart-drawer__footer', '#CartDrawer-Footer', '[id*="cart-drawer-footer"]'];
      for (var fi = 0; fi < footerSels.length; fi++) {
        var curFooter = drawerEl.querySelector(footerSels[fi]);
        var newFooter = drawerDoc.querySelector(footerSels[fi]);
        if (curFooter && newFooter) { curFooter.innerHTML = newFooter.innerHTML; break; }
      }

      var totalSels = ['.totals__total-value', '.cart-drawer__total', '[data-cart-total]'];
      for (var ti = 0; ti < totalSels.length; ti++) {
        var curTotal = drawerEl.querySelector(totalSels[ti]);
        var newTotal = drawerDoc.querySelector(totalSels[ti]);
        if (curTotal && newTotal) { curTotal.innerHTML = newTotal.innerHTML; }
      }

      var newStickyFooter = drawerDoc.querySelector('.ap-sticky-checkout__inner');
      var curStickyFooter = document.querySelector('#cart-drawer .ap-sticky-checkout__inner');
      if (curStickyFooter && newStickyFooter) {
        curStickyFooter.innerHTML = newStickyFooter.innerHTML;
      }
    }

    // Restore scroll so user doesn't get jolted
    if (scrollEl) scrollEl.scrollTop = savedScroll;

    // [May 2026] Refresh upsell only when needed - skip the swap if the live
    // upsell already has populated items. Replacing the populated element
    // with a fresh empty one (then refetching) caused a visible flicker on
    // every cart update.
    var curUpsell = drawerEl.querySelector('#cart-upsell-recommendations');
    var newUpsell = drawerDoc.querySelector('#cart-upsell-recommendations');
    var hasPopulatedUpsell = curUpsell && curUpsell.querySelector('.cart-upsell-compact__item');
    if (curUpsell && newUpsell && !hasPopulatedUpsell) {
      curUpsell.outerHTML = newUpsell.outerHTML;
    }
    if (!hasPopulatedUpsell) {
      setTimeout(function() {
        var upsell = drawerEl.querySelector('product-recommendations#cart-upsell-recommendations');
        if (!upsell) return;
        if (upsell.querySelector('.cart-upsell-compact__item')) return;
        // Patch product_id from cart-item rows if server-rendered with empty value
        if (!upsell.dataset.productId) {
          var firstPaidRow = drawerEl.querySelector('.cart-item:not(.cart-item--freebie)[data-product-id]');
          if (firstPaidRow && firstPaidRow.dataset.productId) {
            var productId = firstPaidRow.dataset.productId;
            upsell.dataset.productId = productId;
            if (upsell.dataset.url) {
              upsell.dataset.url = upsell.dataset.url.replace(/product_id=[^&]*/, 'product_id=' + productId);
            }
          }
        }
        if (typeof upsell.init === 'function') upsell.init();
      }, 0);
    }

    // Clear the updating flag after DOM settles, then trigger line-item:change
    setTimeout(function() {
      window.__AP_SECTIONS_UPDATING__ = false;
      document.dispatchEvent(new CustomEvent('on:line-item:change', { bubbles: true }));
    }, 200);
  }

  // Standalone sections fetch (fallback, used only when we don't have inline sections)
  function refreshCartSections(callbackOrOpts, callback) {
    // Accept either (callback) or (opts, callback)
    var opts = {};
    if (typeof callbackOrOpts === 'function') {
      callback = callbackOrOpts;
    } else if (callbackOrOpts && typeof callbackOrOpts === 'object') {
      opts = callbackOrOpts;
    }
    var url = window.location.pathname + '?sections=' + SECTION_KEYS.join(',');
    fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
    .then(function(res) { return res.ok ? res.json() : null; })
    .then(function(data) {
      applySections(data, opts);
      if (callback) callback();
    })
    .catch(function() { if (callback) callback(); });
  }

  // Helper: lightweight bubble-only update via /cart.js (no section rendering,
  // no DOM mutations inside cart-drawer). Used after drawer-rec adds so the
  // cart count stays accurate without triggering MutationObserver cascades.
  function updateBubbleCount() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(cart) {
        if (!cart) return;
        var badges = document.querySelectorAll('.cart-count-badge, cart-icon-bubble .icon-badge, [data-cart-count]');
        for (var i = 0; i < badges.length; i++) {
          badges[i].textContent = cart.item_count;
        }
      })
      .catch(function() {});
  }

  function formatPrice(cents) {
    var dollars = Math.floor(cents / 100);
    var c = String(cents % 100);
    if (c.length < 2) c = '0' + c;
    return '$' + dollars + '.' + c;
  }

  function handleVariantToggle(wrapper, btn) {
    var group = wrapper.querySelector(SEL.variantGroup);
    if (!group) return;

    var allBtns = group.querySelectorAll(SEL.variantBtn);
    for (var i = 0; i < allBtns.length; i++) { allBtns[i].classList.remove('is-active'); }
    btn.classList.add('is-active');

    wrapper.dataset.variantId = btn.dataset.variantId;

    var priceContainer = wrapper.querySelector(SEL.priceContainer);
    if (priceContainer) {
      var price = btn.dataset.variantPrice || '0';
      var comparePrice = btn.dataset.variantComparePrice || '0';
      var onSale = comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10);
      var html = '';
      if (onSale) {
        html = '<span class="price__current" style="color:rgb(213,62,141);">' + formatPrice(parseInt(price, 10)) + '</span>';
        html += ' <s class="price__was" style="font-size:0.85em;opacity:0.6;">' + formatPrice(parseInt(comparePrice, 10)) + '</s>';
      } else {
        html = '<span class="price__current">' + formatPrice(parseInt(price, 10)) + '</span>';
      }
      priceContainer.innerHTML = html;
    }

    var addBtn = wrapper.querySelector(SEL.addBtn);
    var stepper = wrapper.querySelector('.card__qty-stepper');

    if (btn.disabled) {
      // Sold out variant — show Notify Me
      var variantId = btn.dataset.variantId || wrapper.getAttribute('data-variant-id') || '';
      var productId = wrapper.getAttribute('data-product-id') || '';
      if (stepper) {
        var soldBtn = document.createElement('button');
        soldBtn.type = 'button';
        soldBtn.className = 'btn btn--primary w-full ap-notify-btn';
        soldBtn.textContent = 'Notify Me';
        soldBtn.setAttribute('data-product-id', productId);
        soldBtn.setAttribute('data-variant-id', variantId);
        stepper.parentNode.replaceChild(soldBtn, stepper);
      } else if (addBtn) {
        addBtn.disabled = false;
        addBtn.textContent = 'Notify Me';
        addBtn.classList.remove('btn--added', 'btn--atc', 'js-amazon-atc-btn');
        addBtn.classList.add('ap-notify-btn');
        addBtn.setAttribute('data-product-id', productId);
        addBtn.setAttribute('data-variant-id', variantId);
      }
    } else {
      var vid = parseInt(btn.dataset.variantId, 10);
      var qty = cartVariants[vid] || 0;
      if (qty > 0) {
        // Show stepper for this variant
        if (stepper) {
          stepper.querySelector('.card__qty-count').textContent = qty;
        } else if (addBtn) {
          var div = document.createElement('div');
          div.innerHTML = buildStepperHTML(qty);
          addBtn.parentNode.replaceChild(div.firstChild, addBtn);
        }
      } else {
        // Show pink "Add to cart" button
        if (stepper) {
          var newBtn = document.createElement('button');
          newBtn.type = 'button';
          newBtn.className = 'btn btn--primary w-full btn--atc js-amazon-atc-btn';
          newBtn.textContent = 'Add to cart';
          stepper.parentNode.replaceChild(newBtn, stepper);
        } else if (addBtn) {
          addBtn.textContent = 'Add to cart';
          addBtn.classList.remove('btn--added');
          addBtn.disabled = false;
        }
      }
    }

    // Update all variant pills in this card from cache
    var variantBtns = group.querySelectorAll(SEL.variantBtn);
    for (var v = 0; v < variantBtns.length; v++) {
      var pvid = parseInt(variantBtns[v].dataset.variantId, 10);
      if (cartVariants[pvid]) {
        variantBtns[v].classList.add('in-cart');
      } else {
        variantBtns[v].classList.remove('in-cart');
      }
    }
  }

  function handleAddToCart(wrapper) {
    var variantId = wrapper.dataset.variantId;
    var vid = parseInt(variantId, 10);
    if (!vid) return;

    var addBtn = wrapper.querySelector(SEL.addBtn);
    var prevQty = cartVariants[vid] || 0;

    // Optimistic UI — show stepper + update badge instantly
    cartVariants[vid] = prevQty + 1;
    if (addBtn) {
      var div = document.createElement('div');
      div.innerHTML = buildStepperHTML(cartVariants[vid]);
      addBtn.parentNode.replaceChild(div.firstChild, addBtn);
    }
    var existingStepper = wrapper.querySelector('.card__qty-stepper');
    if (existingStepper) {
      existingStepper.querySelector('.card__qty-count').textContent = cartVariants[vid];
    }
    var pill = wrapper.querySelector(SEL.variantBtn + '.is-active');
    if (pill) pill.classList.add('in-cart');
    updateCartBadgeFromCache();

    var isDesktop = window.matchMedia('(min-width: 769px)').matches;
    if (isDesktop) {
      // Desktop: open drawer immediately
      openCartDrawer();
    } else {
      // Mobile: shake cart icon
      var cartIcon = document.getElementById('cart-icon');
      if (cartIcon) {
        cartIcon.classList.remove('cart-icon--bounce');
        void cartIcon.offsetWidth;
        cartIcon.classList.add('cart-icon--bounce');
        setTimeout(function() { cartIcon.classList.remove('cart-icon--bounce'); }, 1000);
      }
    }

    inFlightCount++;

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: vid,
        quantity: 1,
        sections: isDesktop ? SECTION_KEYS.join(',') : undefined
      })
    })
    .then(function(res) {
      if (!res.ok) return Promise.reject('not ok');
      return res.json();
    })
    .then(function(data) {
      if (!data) return;
      if (isDesktop && data.sections) {
        // Single round-trip: apply sections using our surgical updater
        applySections(data.sections);
      }
    })
    .catch(function() {
      if (prevQty > 0) { cartVariants[vid] = prevQty; } else { delete cartVariants[vid]; }
      applyCartStateToUI();
      updateCartBadgeFromCache();
    })
    .finally(function() {
      inFlightCount--;
      if (inFlightCount === 0) setTimeout(syncButtonsWithCart, 500);
    });
  }

  // --- Product data cache (pre-fetched so clicks are instant) ---
  var productCache = {};

  function getProduct(handle, callback) {
    if (productCache[handle]) { callback(productCache[handle]); return; }
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(product) {
        if (product) productCache[handle] = product;
        callback(product);
      })
      .catch(function() { callback(null); });
  }

  function prefetchDrawerRecs() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var btns = drawer.querySelectorAll('.js-quick-add[data-product-url]');
    for (var i = 0; i < btns.length; i++) {
      var url = btns[i].getAttribute('data-product-url') || '';
      var handle = extractHandle(url);
      if (handle && !productCache[handle]) {
        getProduct(handle, function() {}); // fire-and-forget prefetch
      }
    }
  }

  // --- Cart drawer recommendation: AJAX add-to-cart with tick animation ---

  function handleDrawerRecAdd(btn) {
    if (btn.disabled) return;
    btn.disabled = true;

    var productUrl = btn.getAttribute('data-product-url') || '';
    var handle = extractHandle(productUrl);

    var originalHTML = btn.innerHTML;
    btn.innerHTML = '<span class="drawer-rec-tick">\u2026</span>'; // ellipsis while loading

    if (!handle) {
      btn.innerHTML = originalHTML; btn.disabled = false;       return;
    }

    var addVariant = function(variantId) {
      // IMPORTANT: Do NOT request sections or touch drawer DOM here.
      // Avada Free Gifts intercepts every cart change and updates the drawer
      // itself (milestone, free gifts, etc.). If we also modify drawer DOM,
      // MutationObservers cascade between our code and Avada's, causing an
      // infinite loop of /cart.js polling. Instead we just add the item,
      // show a tick, and update the header cart-count badge.
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1
        })
      })
      .then(function(res) {
        if (!res.ok) {
          btn.innerHTML = '<span style="color:#c00;">Error</span>';
          setTimeout(function() { btn.innerHTML = originalHTML; btn.disabled = false; }, 1500);
                    return;
        }
        return res.json();
      })
      .then(function(json) {
        if (!json) return;
        // Show tick — no drawer DOM changes, Avada + theme handle the rest
        btn.innerHTML = '<span class="drawer-rec-tick">\u2713</span>';
        btn.classList.add('drawer-rec-added');
                // Lightweight badge update only (no sections, no drawer DOM)
        updateBubbleCount();
        setTimeout(function() {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
          btn.classList.remove('drawer-rec-added');
        }, 1200);
      })
      .catch(function() {
        btn.innerHTML = originalHTML; btn.disabled = false;
      });
    };

    // Use cache — if pre-fetched this is instant (no network wait)
    getProduct(handle, function(product) {
      if (!product || !product.variants || !product.variants.length) {
        btn.innerHTML = originalHTML; btn.disabled = false; /* lock removed */ return;
      }
      var avail = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) { avail = product.variants[i]; break; }
      }
      if (!avail) avail = product.variants[0];
      addVariant(avail.id);
    });
  }

  // --- Homepage / collection page: convert .js-quick-add to .js-amazon-atc wrapper, then add ---
  function handleQuickAdd(btn) {
    var productUrl = btn.getAttribute('data-product-url') || '';
    var handle = extractHandle(productUrl);

    if (!handle) return;

    // Show loading state while we fetch the product data
    var originalHTML = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Adding...';

    getProduct(handle, function(product) {
      if (!product || !product.variants || !product.variants.length) {
        btn.innerHTML = originalHTML; btn.disabled = false;
        return;
      }
      var avail = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) { avail = product.variants[i]; break; }
      }
      if (!avail) avail = product.variants[0];

      // Convert the .js-quick-add button into a .js-amazon-atc wrapper
      // so it uses the same stepper/optimistic UI as all other cards
      var wrapper = document.createElement('div');
      wrapper.className = 'js-amazon-atc card__amazon-atc';
      wrapper.setAttribute('data-variant-id', avail.id);
      wrapper.innerHTML = '<button type="button" class="btn btn--primary w-full btn--atc js-amazon-atc-btn">Add to cart</button>';
      btn.parentNode.replaceChild(wrapper, btn);

      // Now trigger the standard add-to-cart flow
      handleAddToCart(wrapper);
    });
  }

  // --- Cart-drawer observer: one-shot prefetch when SS recs first appear ---
  // Scoped tightly to avoid reacting to Avada's constant /cart.js polling
  // which mutates drawer DOM and would cause an observer cascade.
  (function() {
    var recsPrefetched = false;
    var drawerObserver = new MutationObserver(function() {
      if (recsPrefetched) return;
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      var ssResult = drawer.querySelector('.ss__recommendation--side-view-cart .ss__result');
      if (ssResult) {
        recsPrefetched = true;
        prefetchDrawerRecs();
      }
    });
    // Only observe the cart-drawer element itself (not all of document.body)
    // to minimize how often this fires. We start by watching body for the
    // drawer to appear, then re-scope.
    var scopeObserver = function() {
      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawerObserver.observe(drawer, { childList: true, subtree: true });
      } else {
        // Drawer not in DOM yet — watch body briefly, then re-scope
        var bodyObs = new MutationObserver(function() {
          var d = document.querySelector('cart-drawer');
          if (d) {
            bodyObs.disconnect();
            drawerObserver.observe(d, { childList: true, subtree: true });
          }
        });
        bodyObs.observe(document.body, { childList: true, subtree: true });
      }
    };
    // Re-arm prefetch each time drawer opens (so newly rendered recs get prefetched)
    document.addEventListener('click', function(e) {
      if (!e.target || !e.target.closest) return;
      var trigger = e.target.closest('[data-cart-drawer-open], .js-cart-drawer-open, .js-open-cart-drawer, cart-icon-bubble a');
      if (trigger) {
        recsPrefetched = false; // allow re-prefetch on next open
      }
    });
    scopeObserver();
  })();

  function isInsideCartDrawer(el) {
    if (!el || !el.closest) return false;
    return !!el.closest('cart-drawer, #CartDrawer');
  }

  // --- Quantity stepper: +/- handlers with optimistic UI + debounce ---
  var qtyDebounceTimers = {};
  var qtyPendingUpdates = {}; // track pending vid → qty for flush
  function handleQtyChange(wrapper, delta) {
    var vid = parseInt(wrapper.dataset.variantId, 10);
    var oldQty = cartVariants[vid] || 0;
    var newQty = Math.max(0, oldQty + delta);

    // Optimistic UI — update instantly
    if (newQty > 0) {
      cartVariants[vid] = newQty;
    } else {
      delete cartVariants[vid];
    }
    applyCartStateToUI();
    updateCartBadgeFromCache();

    // Debounce: wait 200ms before sending the request
    clearTimeout(qtyDebounceTimers[vid]);
    qtyPendingUpdates[vid] = newQty;
    qtyDebounceTimers[vid] = setTimeout(function() {
      delete qtyPendingUpdates[vid];
      sendQtyUpdate(vid, newQty);
    }, 200);
  }

  // Flush all pending debounced qty updates immediately
  // Called when user navigates to cart so data is up-to-date
  function flushPendingQtyUpdates() {
    Object.keys(qtyPendingUpdates).forEach(function(vid) {
      clearTimeout(qtyDebounceTimers[vid]);
      sendQtyUpdate(parseInt(vid, 10), qtyPendingUpdates[vid]);
    });
    qtyPendingUpdates = {};
  }

  // Intercept clicks on cart links — wait for in-flight requests + flush pending updates
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a[href="/cart"], a[href*="/cart"]');
    if (!link) return;

    var hasPending = Object.keys(qtyPendingUpdates).length > 0;
    var hasInFlight = inFlightCount > 0;

    if (!hasPending && !hasInFlight) return; // nothing pending, navigate normally

    e.preventDefault();
    if (hasPending) flushPendingQtyUpdates();

    // Wait for all in-flight requests to complete, then navigate
    var checkInterval = setInterval(function() {
      if (inFlightCount === 0) {
        clearInterval(checkInterval);
        window.location.href = link.href;
      }
    }, 50);
    // Safety: navigate after 5s max even if requests haven't completed
    setTimeout(function() { clearInterval(checkInterval); window.location.href = link.href; }, 5000);
  }, true);

  function sendQtyUpdate(vid, newQty) {
    inFlightCount++;
    enqueueCartRequest(function() {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: String(vid), quantity: newQty, sections: SECTION_KEYS.join(',') })
      }).then(function(res) { return res.ok ? res.json() : Promise.reject('not ok'); })
      .then(function(json) {
        if (json && json.sections) applySections(json.sections);
      })
      .catch(function() {
        // Sync with server state on error
        syncButtonsWithCart();
      })
      .finally(function() {
        inFlightCount--;
        setTimeout(syncButtonsWithCart, 300);
      });
    });
  }

  // --- Event delegation for theme cards + cart drawer recs ---
  // Uses capture phase to intercept before theme's quick-add popup handler.
  //
  // ⚠️  This handler runs in CAPTURE PHASE (third arg `true` below) which
  //     means it fires BEFORE any bubble-phase listener anywhere on the
  //     page. Calling preventDefault() or stopPropagation() here will
  //     silently kill clicks for everything downstream. If you're adding
  //     a new "if click matches X, do Y" branch, ALWAYS:
  //       1. Use the most specific selector you can
  //       2. Add an exception for any other component that needs the
  //          click to pass through (see cart-upsell-compact exception)
  //       3. Add a code comment explaining the WHY, not just the WHAT
  //
  //     If a click stops working somewhere on the site, suspect this
  //     handler first — wrap Event.prototype.preventDefault to log a
  //     stack trace and find which branch fired.
  document.addEventListener('click', function(e) {
    if (!e.target || !e.target.closest) return;

    // Quantity stepper +/- buttons
    var qtyMinus = e.target.closest('.js-qty-minus');
    if (qtyMinus) {
      e.preventDefault();
      e.stopPropagation();
      var wrapper = qtyMinus.closest(SEL.wrapper);
      if (wrapper) handleQtyChange(wrapper, -1);
      return;
    }
    var qtyPlus = e.target.closest('.js-qty-plus');
    if (qtyPlus) {
      e.preventDefault();
      e.stopPropagation();
      var wrapper = qtyPlus.closest(SEL.wrapper);
      if (wrapper) handleQtyChange(wrapper, 1);
      return;
    }

    var variantBtn = e.target.closest(SEL.variantBtn);
    if (variantBtn) {
      e.preventDefault();
      var wrapper = variantBtn.closest(SEL.wrapper);
      if (wrapper) handleVariantToggle(wrapper, variantBtn);
      return;
    }

    var addBtn = e.target.closest(SEL.addBtn);
    if (addBtn) {
      e.preventDefault();
      e.stopPropagation();
      var wrapper = addBtn.closest(SEL.wrapper);
      if (wrapper) handleAddToCart(wrapper);
      return;
    }

    // Intercept ALL .js-quick-add buttons (SS cards that weren't enhanced)
    var quickAddBtn = e.target.closest('.js-quick-add');
    if (quickAddBtn) {
      e.preventDefault();
      e.stopPropagation();
      if (isInsideCartDrawer(quickAddBtn)) {
        handleDrawerRecAdd(quickAddBtn);
      } else {
        handleQuickAdd(quickAddBtn);
      }
      return;
    }

    // Prevent SS card image/title links inside cart drawer from navigating
    // [May 5, 2026] EXCEPT for cart-upsell-compact tiles - those are our own
    // upsell carousel where users explicitly want to click through to the PDP.
    // Without this exception, all upsell tile clicks were blocked in the
    // drawer (Microsoft Clarity confirmed rage clicks).
    var link = e.target.closest('a');
    if (link && isInsideCartDrawer(link) && !link.closest('.cart-upsell-compact__item')) {
      var inRec = link.closest('product-recommendations, .cart-recommendations, [class*="ss__recommendation"]');
      if (inRec) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
  }, true);

  // --- Judge.me badge injection for SS tiles via Shopify Section Rendering ---
  var jdgmBadgeCache = {};

  function injectJdgmBadgeAsync(card, handle) {
    if (card.querySelector('.jdgm-preview-badge')) return;

    function applyBadge(html) {
      if (!html || card.querySelector('.jdgm-preview-badge')) return;
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var badge = tmp.querySelector('.jdgm-preview-badge');
      // Skip if badge has no content (product has no reviews yet)
      if (!badge || !badge.firstChild) return;
      var titleEl = card.querySelector('.card__title, .ss__result__name');
      if (titleEl && titleEl.parentNode) {
        titleEl.parentNode.insertBefore(badge, titleEl.nextSibling);
        if (window.jdgm && typeof jdgm.initializeWidgets === 'function') {
          try { jdgm.initializeWidgets(); } catch (e) {}
        }
      }
    }

    if (handle in jdgmBadgeCache) {
      applyBadge(jdgmBadgeCache[handle]);
      return;
    }

    fetch('/products/' + handle + '?sections=jdgm-badge')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = (data && data['jdgm-badge']) || '';
        jdgmBadgeCache[handle] = html;
        applyBadge(html);
      })
      .catch(function () {
        jdgmBadgeCache[handle] = '';
      });
  }

  // --- Enhance SearchSpring cards with variant pills on load ---
  var ssProcessed = {};

  function enhanceSSCard(card) {
    // Skip cards inside the cart drawer — their .js-quick-add buttons are
    // intercepted via handleDrawerRecAdd. Modifying the Preact-managed DOM
    // would corrupt the SS virtual-DOM and unmount the recommendation widget.
    if (isInsideCartDrawer(card)) return;

    var quickAdd = card.querySelector('.js-quick-add');
    if (!quickAdd) return;

    // If our wrapper already exists (with stepper or button), remove
    // the Preact-reinserted .js-quick-add so the observer stops firing
    var existingWrapper = card.querySelector('.js-amazon-atc');
    if (existingWrapper) {
      quickAdd.remove();
      return;
    }

    var productId = quickAdd.getAttribute('data-product-id');
    if (!productId || ssProcessed[productId]) return;
    ssProcessed[productId] = true;

    var productUrl = quickAdd.getAttribute('data-product-url') || '';
    var handle = extractHandle(productUrl);
    if (!handle) return;

    // Fetch Judge.me badge via section rendering (parallel to getProduct)
    injectJdgmBadgeAsync(card, handle);

    getProduct(handle, function(product) {
        if (!product || !product.variants) return;

        // Inject Growave wishlist placeholder if not already present
        var cardMedia = card.querySelector('.ss__result__image-wrapper, .card__media, .ss__result__image');
        if (cardMedia && !card.querySelector('.gw-add-to-wishlist-product-card-placeholder')) {
          var gwDiv = document.createElement('div');
          gwDiv.style.display = 'block';
          gwDiv.className = 'gw-add-to-wishlist-product-card-placeholder';
          gwDiv.setAttribute('data-gw-product-id', productId);
          var firstVariant = product.variants[0];
          if (firstVariant) gwDiv.setAttribute('data-gw-variant-id', String(firstVariant.id));
          gwDiv.setAttribute('data-gw-wishlist-counter-position', 'right-top');
          cardMedia.style.position = 'relative';
          cardMedia.appendChild(gwDiv);
        }

        // Mix & Match bundles — show link to product page instead of ATC
        if (product.tags && product.tags.indexOf('Mix & Match') !== -1) {
          quickAdd.outerHTML = '<a href="' + productUrl + '" class="btn btn--primary w-full btn--atc">Mix &amp; Match</a>';
          return;
        }

        var variants = product.variants;

        if (variants.length <= 1) {
          var vi = variants[0];
          var hasRealVariant = vi.title && vi.title !== 'Default Title';
          var html = '<div class="js-amazon-atc card__amazon-atc" data-variant-id="' + vi.id + '" data-product-id="' + productId + '">';
          // Show variant pill only if it has a real name (e.g. "Each"), skip for "Default Title"
          if (hasRealVariant) {
            html += '<div class="js-variant-toggle card__variant-toggle">';
            html += '<button type="button" class="js-variant-toggle-btn card__variant-btn is-active"';
            html += ' data-variant-id="' + vi.id + '"';
            html += ' data-variant-price="' + vi.price + '"';
            html += ' data-variant-compare-price="' + (vi.compare_at_price || 0) + '"';
            if (!vi.available) html += ' disabled title="Sold out"';
            html += '>' + vi.title.replace(/</g, '&lt;') + '</button>';
            html += '</div>';
          }
          if (vi.available) {
            html += '<button type="button" class="btn btn--primary w-full btn--atc js-amazon-atc-btn">Add to cart</button>';
          } else {
            html += '<button type="button" class="btn btn--primary w-full ap-notify-btn" data-product-id="' + productId + '" data-variant-id="' + vi.id + '">Notify Me</button>';
          }
          html += '</div>';
          quickAdd.outerHTML = html;

        } else if (variants.length >= 2 && variants.length <= 4) {
          // 2–4 variants: show first 2 variant pills so user can pick
          var first = variants[0];
          var pillCount = Math.min(variants.length, 2); // Cap at 2 pills

          var themePrice = card.querySelector('.ss__result__pricing, .price--bottom');
          if (themePrice) themePrice.style.display = 'none';

          var priceHtml = '<div class="js-variant-price card__variant-price">';
          if (first.compare_at_price && first.compare_at_price > first.price) {
            priceHtml += '<span class="price__current" style="color:rgb(213,62,141);">' + formatPrice(first.price) + '</span>';
            priceHtml += ' <s class="price__was" style="font-size:0.85em;opacity:0.6;">' + formatPrice(first.compare_at_price) + '</s>';
          } else {
            priceHtml += '<span class="price__current">' + formatPrice(first.price) + '</span>';
          }
          priceHtml += '</div>';

          var toggleHtml = '<div class="js-variant-toggle card__variant-toggle">';
          for (var j = 0; j < pillCount; j++) {
            var variant = variants[j];
            toggleHtml += '<button type="button" class="js-variant-toggle-btn card__variant-btn' + (j === 0 ? ' is-active' : '') + '"';
            toggleHtml += ' data-variant-id="' + variant.id + '"';
            toggleHtml += ' data-variant-price="' + variant.price + '"';
            toggleHtml += ' data-variant-compare-price="' + (variant.compare_at_price || 0) + '"';
            if (!variant.available) toggleHtml += ' disabled title="Sold out"';
            toggleHtml += '>' + variant.title.replace(/</g, '&lt;') + '</button>';
          }
          toggleHtml += '</div>';

          var btnHtml;
          if (product.available) {
            btnHtml = '<button type="button" class="btn btn--primary w-full btn--atc js-amazon-atc-btn">Add to cart</button>';
          } else {
            btnHtml = '<button type="button" class="btn btn--primary w-full ap-notify-btn" data-product-id="' + productId + '" data-variant-id="' + first.id + '">Notify Me</button>';
          }

          var wrapper = document.createElement('div');
          wrapper.className = 'js-amazon-atc card__amazon-atc';
          wrapper.setAttribute('data-variant-id', first.id);
          wrapper.setAttribute('data-product-id', productId);
          wrapper.innerHTML = priceHtml + toggleHtml + btnHtml;
          quickAdd.parentNode.replaceChild(wrapper, quickAdd);

        } else {
          // 5+ variants: too many for pills, just show "Add to cart" with first available
          var avail = null;
          for (var i = 0; i < variants.length; i++) {
            if (variants[i].available) { avail = variants[i]; break; }
          }
          if (!avail) avail = variants[0];
          var html = '<div class="js-amazon-atc card__amazon-atc" data-variant-id="' + avail.id + '" data-product-id="' + productId + '">';
          if (avail.available) {
            html += '<button type="button" class="btn btn--primary w-full btn--atc js-amazon-atc-btn">Add to cart</button>';
          } else {
            html += '<button type="button" class="btn btn--primary w-full ap-notify-btn" data-product-id="' + productId + '" data-variant-id="' + avail.id + '">Notify Me</button>';
          }
          html += '</div>';
          quickAdd.outerHTML = html;
        }
    });
  }

  // Tell Growave to re-scan DOM for new wishlist placeholders
  function triggerGrowaveRescan() {
    // Growave listens for this custom event to re-init wishlist buttons
    setTimeout(function() {
      document.dispatchEvent(new CustomEvent('growave:wishlist:init'));
      // Fallback: Growave also watches for DOM changes via MutationObserver
      // The placeholder injection above should trigger it naturally.
      // Additional fallback: try calling Growave's global init if available
      if (window.GrowaveWishlist && typeof window.GrowaveWishlist.init === 'function') {
        window.GrowaveWishlist.init();
      }
      if (window.ssw && typeof window.ssw.init === 'function') {
        window.ssw.init();
      }
    }, 500);
  }

  function enhanceAllSSCards() {
    var cards = document.querySelectorAll('.ss__result, [class*="ss__result--item"]');
    var delay = 0;
    for (var i = 0; i < cards.length; i++) {
      (function(card, d, isLast) {
        setTimeout(function() {
          enhanceSSCard(card);
          // After last card is enhanced, re-apply cart state so
          // newly created buttons get the correct Added/in-cart state
          if (isLast) {
            applyCartStateToUI();
            // Trigger Growave to re-scan for new wishlist placeholders
            triggerGrowaveRescan();
          }
        }, d);
      })(cards[i], delay, i === cards.length - 1);
      delay += 50;
    }
  }

  // Debounced SS card observer — watches #main (or body fallback) for new SS cards.
  // Scoped to #main to avoid firing on cart-drawer / Avada mutations in the header.
  var enhanceTimer = null;
  var ssCardObserver = new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var target = mutations[i].target;
      if (!target || !target.querySelector) continue;
      if (target.closest && target.closest('cart-drawer, #CartDrawer')) continue;
      if (target.querySelector('.ss__result .js-quick-add')) {
        ssProcessed = {};  // Clear cache so new DOM nodes from Snap re-renders get processed
        if (enhanceTimer) clearTimeout(enhanceTimer);
        enhanceTimer = setTimeout(enhanceAllSSCards, 500);
        break;
      }
    }
  });
  // Scope observer to #main content area to avoid reacting to Avada's
  // header/drawer mutations. Falls back to body if #main not found.
  var ssObserveTarget = document.getElementById('main') || document.getElementById('MainContent') || document.querySelector('main') || document.body;
  ssCardObserver.observe(ssObserveTarget, { childList: true, subtree: true });

  if (document.readyState === 'complete') {
    setTimeout(enhanceAllSSCards, 1500);
  } else {
    window.addEventListener('load', function() {
      setTimeout(enhanceAllSSCards, 1500);
    });
  }

  // --- Local cart cache for instant UI updates (no network delay) ---
  // Pre-loaded from Liquid (window.__cartVariants) — available instantly, no fetch
  var cartVariants = window.__cartVariants || {};

  function rebuildCartCache(cart) {
    cartVariants = {};
    if (!cart || !cart.items) return;
    for (var i = 0; i < cart.items.length; i++) {
      cartVariants[cart.items[i].variant_id] = cart.items[i].quantity;
    }
  }

  // Instantly update the cart badge count from local cache (no network wait)
  function updateCartBadgeFromCache() {
    var total = 0;
    for (var k in cartVariants) { total += cartVariants[k]; }
    var badges = document.querySelectorAll('.header__cart-count-number, cart-icon-bubble .icon-badge, [data-cart-count]');
    for (var i = 0; i < badges.length; i++) { badges[i].textContent = total; }
    // Show/hide the badge container
    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble) {
      if (total > 0) {
        if (!bubble.querySelector('.header__cart-count')) {
          bubble.innerHTML = '<div class="header__cart-count absolute bg-theme-text text-theme-bg text-center"><span aria-hidden="true" class="header__cart-count-number">' + total + '</span></div>';
        }
      } else {
        bubble.innerHTML = '';
      }
    }
  }

  function buildStepperHTML(qty) {
    return '<div class="card__qty-stepper">' +
      '<button type="button" class="js-qty-minus" aria-label="Decrease quantity">&minus;</button>' +
      '<span class="card__qty-count">' + qty + '</span>' +
      '<button type="button" class="js-qty-plus" aria-label="Increase quantity">+</button>' +
      '</div>';
  }

  // Update all buttons and pills from the local cache (synchronous, instant)
  function applyCartStateToUI() {
    var wrappers = document.querySelectorAll(SEL.wrapper);
    for (var w = 0; w < wrappers.length; w++) {
      var vid = parseInt(wrappers[w].dataset.variantId, 10);
      var qty = cartVariants[vid] || 0;
      var btn = wrappers[w].querySelector(SEL.addBtn);
      var stepper = wrappers[w].querySelector('.card__qty-stepper');

      if (qty > 0) {
        // Show stepper
        if (stepper) {
          stepper.querySelector('.card__qty-count').textContent = qty;
        } else if (btn) {
          var div = document.createElement('div');
          div.innerHTML = buildStepperHTML(qty);
          btn.parentNode.replaceChild(div.firstChild, btn);
        }
      } else {
        // Show "Add to cart" button
        if (stepper) {
          var newBtn = document.createElement('button');
          newBtn.type = 'button';
          newBtn.className = 'btn btn--primary w-full btn--atc js-amazon-atc-btn';
          newBtn.textContent = 'Add to cart';
          stepper.parentNode.replaceChild(newBtn, stepper);
        } else if (btn) {
          btn.textContent = 'Add to cart';
          btn.classList.remove('btn--added');
        }
      }

      // Update variant pills
      var pills = wrappers[w].querySelectorAll(SEL.variantBtn);
      for (var p = 0; p < pills.length; p++) {
        var pvid = parseInt(pills[p].dataset.variantId, 10);
        if (cartVariants[pvid]) {
          pills[p].classList.add('in-cart');
        } else {
          pills[p].classList.remove('in-cart');
        }
      }
    }
  }

  // Fetch cart from server and refresh cache + UI
  // Skips if requests are in-flight to avoid overwriting optimistic state
  function syncButtonsWithCart() {
    if (inFlightCount > 0) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(cart) {
        if (inFlightCount > 0) return; // request started while we were fetching
        rebuildCartCache(cart);
        applyCartStateToUI();
        updateCartBadgeFromCache();
      })
      .catch(function() {});
  }

  // Debounced sync — multiple rapid cart events (e.g., add + free gift)
  // collapse into a single sync after everything settles
  var syncTimer = null;
  function debouncedSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncButtonsWithCart, 1500);
  }
  ['cart:updated', 'cart:refresh', 'cart:change'].forEach(function(ev) {
    document.addEventListener(ev, debouncedSync);
  });
  document.addEventListener('on:line-item:change', debouncedSync);

  // Apply cart state as soon as DOM is ready (uses pre-loaded data, no fetch)
  // Apply cart state on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { applyCartStateToUI(); });
  } else {
    applyCartStateToUI();
  }

  // Re-apply after cards are enhanced (SS/card enhancement runs after DOMContentLoaded)
  // This catches single-variant .js-quick-add buttons that need converting to steppers
  setTimeout(function() { applyCartStateToUI(); convertQuickAddButtons(); }, 1000);
  setTimeout(function() { applyCartStateToUI(); convertQuickAddButtons(); }, 2500);

  // Force sync — bypasses inFlightCount check
  function forceSyncCart() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(cart) {
        if (!cart) return;
        cartVariants = {};
        cart.items.forEach(function(item) {
          cartVariants[item.variant_id] = (cartVariants[item.variant_id] || 0) + item.quantity;
        });
        applyCartStateToUI();
        updateCartBadgeFromCache();
        // Also convert .js-quick-add buttons to steppers if their product is in cart
        convertQuickAddButtons();
      })
      .catch(function() {});
  }

  // Convert .js-quick-add buttons to .js-amazon-atc wrappers if the product is in the cart
  function convertQuickAddButtons() {
    var quickAddBtns = document.querySelectorAll('.js-quick-add[data-product-url]');
    for (var i = 0; i < quickAddBtns.length; i++) {
      var btn = quickAddBtns[i];
      var url = btn.getAttribute('data-product-url') || '';
      var handle = extractHandle(url);
      if (!handle) continue;

      getProduct(handle, (function(b) {
        return function(product) {
          if (!product || !product.variants) return;
          for (var v = 0; v < product.variants.length; v++) {
            var vid = product.variants[v].id;
            if (cartVariants[vid]) {
              // This variant is in the cart — convert button to wrapper + stepper
              var wrapper = document.createElement('div');
              wrapper.className = 'js-amazon-atc card__amazon-atc';
              wrapper.setAttribute('data-variant-id', vid);
              wrapper.innerHTML = buildStepperHTML(cartVariants[vid]);
              if (b.parentNode) b.parentNode.replaceChild(wrapper, b);
              break;
            }
          }
        };
      })(btn));
    }
  }

  // Preload drawer sections on page load so first refresh is faster (cached by browser)
  if (window.matchMedia('(min-width: 769px)').matches) {
    window.addEventListener('load', function() {
      var url = '/?sections=' + SECTION_KEYS.join(',');
      fetch(url, { credentials: 'same-origin' }).catch(function() {});
    });
  }

  // Sync cart state on back/forward navigation AND page restore from bfcache
  window.addEventListener('pageshow', function(e) {
    if (e.persisted) {
      forceSyncCart();
    }
  });

  // Also sync on visibility change (tab switch back)
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible' && inFlightCount === 0) {
      syncButtonsWithCart();
    }
  });

})();